import * as React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { useNavigate, useParams } from "react-router-dom";
import { useDataProvider, useGetIdentity, useNotify } from "react-admin";
import { formatDate } from "../../utils/common";
import { getTruckPlateColorLabel } from "../../models/Truck";
import type {
  CreateTicketReplyInput,
  TicketDetail,
  TicketReply,
} from "../../models/Ticket";
import type { AppDataProvider } from "../../types/dataProvider";
import { TruckLength, TruckType } from "../../utils/truck-utils";
import {
  getSenderDisplayName,
  getTicketTypeColor,
  getTicketTypeLabel,
  isAiReply,
  isReplyFromCurrentUser,
} from "./utils";

const MAX_IMAGES = 3;

interface ComposerImage {
  id: string;
  file: File;
  url: string;
  name: string;
  size: number;
}

/**
 * 参与者车辆展示信息。
 */
interface MemberTruckInfo {
  id: string;
  plate: string;
  plateColor: string;
  truckType: string;
  truckLength: string;
  phone: string;
}

interface UserRecord {
  id?: string;
  _id?: string;
  name?: string;
  fullName?: string;
  username?: string;
  /** 服务端统一字段：真实姓名 */
  real_name?: string;
  realname?: string;
  nickname?: string;
  avatar?: string;
  photo?: string;
  headimgurl?: string;
  /** 服务端统一字段：手机号 */
  phone?: string;
  mobile?: string;
  phoneNumber?: string;
  /** 服务端统一字段：邮箱 */
  email?: string;
  /** 司机车辆列表（users / drivers 接口可能带回） */
  trucks?: unknown[];
  Vehicles?: unknown[];
  vehicles?: unknown[];
}

const parseStringValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return "";
};

const getUserRecordId = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return parseStringValue(record.id ?? record._id);
};

/**
 * 从用户记录中解析真实姓名（优先服务端统一字段 real_name）。
 * @param record 用户记录
 * @returns 真实姓名，不存在时返回空字符串
 */
const getUserDisplayRealName = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return parseStringValue(record.real_name ?? record.realname);
};

/**
 * 从用户记录中解析展示名称。
 * @param record 用户记录
 * @returns 展示名称，不存在时返回空字符串
 */
const getUserDisplayName = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return (
    parseStringValue(record.name) ||
    parseStringValue(record.fullName) ||
    getUserDisplayRealName(record) ||
    parseStringValue(record.username) ||
    parseStringValue(record.nickname)
  );
};

/**
 * 从用户记录中解析头像地址。
 * @param record 用户记录
 * @returns 头像 URL，不存在时返回空字符串
 */
const getUserDisplayAvatar = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return parseStringValue(record.avatar ?? record.photo ?? record.headimgurl);
};

/**
 * 从用户记录中解析手机号（优先服务端统一字段 phone）。
 * @param record 用户记录
 * @returns 手机号字符串，不存在时返回空字符串
 */
const getUserDisplayPhone = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return parseStringValue(record.phone ?? record.mobile ?? record.phoneNumber);
};

/**
 * 从用户记录中解析邮箱（优先服务端统一字段 email）。
 * @param record 用户记录
 * @returns 邮箱字符串，不存在时返回空字符串
 */
const getUserDisplayEmail = (record: UserRecord | null): string => {
  if (!record) {
    return "";
  }
  return parseStringValue(record.email);
};

/**
 * 将未知值解析为有限数字，无效时返回 null。
 * @param value 原始值
 * @returns 数字或 null
 */
const pickNumberValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * 解析单条车辆记录为展示结构。
 * @param raw 原始车辆数据
 * @returns 车辆展示信息，无法识别时返回 null
 */
const parseTruckInfo = (raw: unknown): MemberTruckInfo | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const truck = raw as Record<string, unknown>;
  const plate = parseStringValue(truck.plate ?? truck.licensePlate ?? truck.license_plate);
  const typeText = parseStringValue(truck.type);
  const truckTypeCode = pickNumberValue(truck.truck_type ?? truck.truckType);
  const truckLengthCode = pickNumberValue(truck.truck_length ?? truck.truckLength);
  const plateColorCode = pickNumberValue(truck.plate_color ?? truck.plateColor);
  const phone = parseStringValue(truck.phone);
  const id = parseStringValue(truck.id ?? truck._id);

  if (!id && !plate && !typeText && truckTypeCode === null) {
    return null;
  }

  return {
    id,
    plate: plate || "-",
    plateColor: plateColorCode !== null ? getTruckPlateColorLabel(plateColorCode) : "-",
    truckType: truckTypeCode !== null ? TruckType(truckTypeCode) : typeText || "-",
    truckLength: truckLengthCode !== null ? TruckLength(truckLengthCode) : "-",
    phone,
  };
};

/**
 * 从用户 / 司机记录中解析车辆列表。
 * @param record 用户或司机记录
 * @returns 车辆展示列表
 */
const parseTrucksFromRecord = (record: UserRecord | null): MemberTruckInfo[] => {
  if (!record) {
    return [];
  }
  const trucksRaw = record.trucks ?? record.Vehicles ?? record.vehicles;
  if (!Array.isArray(trucksRaw)) {
    return [];
  }
  return trucksRaw
    .map((item) => parseTruckInfo(item))
    .filter((item): item is MemberTruckInfo => item !== null);
};

const generateImageId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const CommunicationDetailPage: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const currentUserId = React.useMemo(() => {
    if (identity && typeof identity === "object" && "id" in identity) {
      const id = (identity as { id?: string | null }).id;
      return id || null;
    }
    return null;
  }, [identity]);
  const currentUserRecord = React.useMemo<UserRecord | null>(() => {
    if (!identity || typeof identity !== "object") {
      return null;
    }
    const identityRecord = identity as Record<string, unknown>;
    const id = parseStringValue(identityRecord.id);
    if (!id) {
      return null;
    }
    return {
      id,
      fullName: parseStringValue(identityRecord.fullName),
      name: parseStringValue(identityRecord.name),
      username: parseStringValue(identityRecord.username),
      nickname: parseStringValue(identityRecord.nickname),
      real_name: parseStringValue(identityRecord.real_name),
      realname: parseStringValue(identityRecord.realname),
      phone: parseStringValue(identityRecord.phone),
      mobile: parseStringValue(identityRecord.mobile),
      phoneNumber: parseStringValue(identityRecord.phoneNumber),
      email: parseStringValue(identityRecord.email),
      avatar: parseStringValue(identityRecord.avatar),
      photo: parseStringValue(identityRecord.photo),
      headimgurl: parseStringValue(identityRecord.headimgurl),
    };
  }, [identity]);

  const [detail, setDetail] = React.useState<TicketDetail | null>(null);
  const [memberUsersById, setMemberUsersById] = React.useState<Record<string, UserRecord>>({});
  const [memberTrucksById, setMemberTrucksById] = React.useState<Record<string, MemberTruckInfo[]>>({});
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [composerText, setComposerText] = React.useState<string>("");
  const [composerImages, setComposerImages] = React.useState<ComposerImage[]>([]);
  const [sending, setSending] = React.useState(false);
  const [previewImages, setPreviewImages] = React.useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = React.useState<number>(-1);
  const [previewScale, setPreviewScale] = React.useState<number>(1);
  const [previewRotation, setPreviewRotation] = React.useState<number>(0);
  const [pendingDeleteReply, setPendingDeleteReply] = React.useState<TicketReply | null>(null);
  const [deletingReplyId, setDeletingReplyId] = React.useState<string | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const ticketIdRef = React.useRef<string | undefined>(ticketId);
  React.useEffect(() => {
    ticketIdRef.current = ticketId;
  }, [ticketId]);

  const fetchDetail = React.useCallback(
    async (id: string) => {
      setLoading(true);
      setErrorMessage("");
      try {
        const response = await dataProvider.getTicketDetail(id);
        setDetail(response.data);
        setErrorMessage("");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "沟通工单获取失败";
        setErrorMessage(message);
        setDetail(null);
      } finally {
        setLoading(false);
      }
    },
    [dataProvider]
  );

  React.useEffect(() => {
    if (ticketId) {
      void fetchDetail(ticketId);
    } else {
      setLoading(false);
      setErrorMessage("沟通工单获取失败");
      setDetail(null);
    }
  }, [fetchDetail, ticketId]);

  React.useEffect(() => {
    return () => {
      composerImages.forEach((image) => URL.revokeObjectURL(image.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const memberIds = detail?.members ?? [];
    if (memberIds.length === 0) {
      setMemberUsersById({});
      setMemberTrucksById({});
      return;
    }

    let cancelled = false;
    const uniqueMemberIds = Array.from(new Set(memberIds.filter(Boolean)));

    const fetchMemberUsers = async () => {
      try {
        const [usersResponse, driversResponse] = await Promise.all([
          dataProvider.getMany("users", { ids: uniqueMemberIds }),
          dataProvider
            .getMany("drivers", { ids: uniqueMemberIds })
            .catch(() => ({ data: [] as unknown[] })),
        ]);
        if (cancelled) {
          return;
        }
        const userRecords = Array.isArray(usersResponse.data) ? usersResponse.data : [];
        const driverRecords = Array.isArray(driversResponse.data) ? driversResponse.data : [];
        const nextUsers: Record<string, UserRecord> = {};
        const nextTrucks: Record<string, MemberTruckInfo[]> = {};

        userRecords.forEach((item) => {
          if (!item || typeof item !== "object") {
            return;
          }
          const record = item as UserRecord;
          const id = getUserRecordId(record);
          if (!id) {
            return;
          }
          nextUsers[id] = record;
          const trucks = parseTrucksFromRecord(record);
          if (trucks.length > 0) {
            nextTrucks[id] = trucks;
          }
        });

        // drivers 接口通常带完整车辆信息，优先覆盖 users 中的车辆
        driverRecords.forEach((item) => {
          if (!item || typeof item !== "object") {
            return;
          }
          const record = item as UserRecord;
          const id = getUserRecordId(record);
          if (!id) {
            return;
          }
          if (!nextUsers[id]) {
            nextUsers[id] = record;
          }
          const trucks = parseTrucksFromRecord(record);
          if (trucks.length > 0) {
            nextTrucks[id] = trucks;
          }
        });

        setMemberUsersById(nextUsers);
        setMemberTrucksById(nextTrucks);
      } catch {
        if (!cancelled) {
          setMemberUsersById({});
          setMemberTrucksById({});
        }
      }
    };

    void fetchMemberUsers();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, detail?.members]);

  const handleBack = () => {
    navigate("/communication");
  };

  const handleImageButtonClick = () => {
    if (sending) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) {
      return;
    }
    const remainingSlots = MAX_IMAGES - composerImages.length;
    if (remainingSlots <= 0) {
      notify("最多上传 3 张图片", { type: "warning" });
      return;
    }
    const accepted = files
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, remainingSlots);
    if (accepted.length < files.length) {
      notify("已忽略非图片或超出 3 张上限的文件", { type: "warning" });
    }
    if (accepted.length === 0) {
      return;
    }
    const nextImages: ComposerImage[] = accepted.map((file) => {
      const nextImage: ComposerImage = {
        id: generateImageId(),
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      };
      return nextImage;
    });
    setComposerImages((previous) => [...previous, ...nextImages]);
  };

  const handleRemoveComposerImage = (id: string) => {
    setComposerImages((previous) => {
      const target = previous.find((image) => image.id === id);
      if (target) {
        URL.revokeObjectURL(target.url);
      }
      return previous.filter((image) => image.id !== id);
    });
  };

  const handleSend = async () => {
    if (sending) {
      return;
    }
    const trimmed = composerText.trim();
    if (!trimmed) {
      notify("请输入回复内容", { type: "warning" });
      return;
    }
    const currentTicketId = ticketIdRef.current;
    if (!currentTicketId) {
      notify("沟通工单获取失败", { type: "error" });
      return;
    }
    setSending(true);
    try {
      const uploadedImages = await Promise.all(
        composerImages.map(async (image) => {
          const result = await dataProvider.uploadTicketPublicImage(image.file);
          return result.data.url;
        })
      );
      if (uploadedImages.some((url) => url.startsWith("blob:"))) {
        throw new Error("图片上传未返回可访问链接");
      }
      const payload: CreateTicketReplyInput = {
        content: trimmed,
        images: uploadedImages,
      };
      const response = await dataProvider.createTicketReply(currentTicketId, payload);
      const replies = detail?.replies ?? [];
      const optimisticReply: TicketReply = response.data?.id
        ? response.data
        : {
            id: `temp-${Date.now()}`,
            content: trimmed,
            images: uploadedImages,
            createdAt: new Date().toISOString(),
            isSelf: true,
          };
      const nextDetail: TicketDetail = {
        id: detail?.id ?? currentTicketId,
        type: detail?.type ?? "",
        title: detail?.title ?? "",
        createdAt: detail?.createdAt ?? "",
        updatedAt: new Date().toISOString(),
        members: detail?.members ?? [],
        replies: [...replies, optimisticReply],
      };
      setDetail(nextDetail);
      composerImages.forEach((image) => URL.revokeObjectURL(image.url));
      setComposerImages([]);
      setComposerText("");
      notify(composerImages.length > 0 ? "图片上传并发送成功" : "已发送回复", { type: "success" });
      void fetchDetail(currentTicketId);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "发送失败";
      notify(`发送失败：${message}`, { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteReplyRequest = (reply: TicketReply) => {
    if (!reply.id || deletingReplyId) {
      return;
    }
    setPendingDeleteReply(reply);
  };

  const handleDeleteReplyCancel = () => {
    if (deletingReplyId) {
      return;
    }
    setPendingDeleteReply(null);
  };

  const handleDeleteReplyConfirm = async () => {
    if (!pendingDeleteReply?.id) {
      return;
    }
    const currentTicketId = ticketIdRef.current;
    if (!currentTicketId) {
      return;
    }
    setDeletingReplyId(pendingDeleteReply.id);
    try {
      await dataProvider.deleteTicketReply({
        ticketId: currentTicketId,
        replyId: pendingDeleteReply.id,
      });
      setDetail((previous) => {
        if (!previous) {
          return previous;
        }
        return {
          ...previous,
          replies: previous.replies.filter(
            (item) => item.id !== pendingDeleteReply.id
          ),
        };
      });
      setPendingDeleteReply(null);
      notify("已删除回复", { type: "success" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "删除失败";
      notify(`删除失败：${message}`, { type: "error" });
    } finally {
      setDeletingReplyId(null);
    }
  };

  const openPreview = (images: string[], index: number) => {
    setPreviewImages(images);
    setPreviewIndex(index);
    setPreviewScale(1);
    setPreviewRotation(0);
  };

  const closePreview = () => {
    setPreviewImages([]);
    setPreviewIndex(-1);
    setPreviewScale(1);
    setPreviewRotation(0);
  };

  const handlePreviewZoomIn = () => {
    setPreviewScale((previous) => Math.min(previous + 0.25, 3));
  };

  const handlePreviewZoomOut = () => {
    setPreviewScale((previous) => Math.max(previous - 0.25, 0.5));
  };

  const handlePreviewRotateLeft = () => {
    setPreviewRotation((previous) => previous - 90);
  };

  const handlePreviewRotateRight = () => {
    setPreviewRotation((previous) => previous + 90);
  };

  const handlePreviewReset = () => {
    setPreviewScale(1);
    setPreviewRotation(0);
  };

  const handlePreviewKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (previewImages.length === 0) {
      return;
    }
    if (event.key === "ArrowRight") {
      setPreviewIndex((previous) =>
        previous + 1 >= previewImages.length ? 0 : previous + 1
      );
    } else if (event.key === "ArrowLeft") {
      setPreviewIndex((previous) =>
        previous - 1 < 0 ? previewImages.length - 1 : previous - 1
      );
    } else if (event.key === "Escape") {
      closePreview();
    }
  };

  const renderConversationPanel = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={320}
        >
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (errorMessage || !detail) {
      return <Alert severity="error">{errorMessage || "沟通工单获取失败"}</Alert>;
    }

    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              color={getTicketTypeColor(detail.type)}
              label={getTicketTypeLabel(detail.type)}
            />
            <Typography variant="subtitle1" fontWeight={700}>
              {detail.title || "沟通工单"}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            工单 ID：{detail.id || "-"}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              参与者
            </Typography>
            {detail.members.length > 0 ? (
              <Stack spacing={1}>
                {detail.members.map((memberId) => {
                  const user = memberUsersById[memberId] ?? null;
                  const name = getUserDisplayName(user);
                  const avatar = getUserDisplayAvatar(user);
                  const realName = getUserDisplayRealName(user);
                  const phone = getUserDisplayPhone(user);
                  const email = getUserDisplayEmail(user);
                  const trucks = memberTrucksById[memberId] ?? [];
                  return (
                    <Stack
                      key={memberId}
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                      sx={{
                        px: 1.25,
                        py: 1,
                        borderRadius: 1.5,
                        bgcolor: "background.default",
                      }}
                    >
                      <Avatar src={avatar || undefined} alt={name || memberId} sx={{ width: 32, height: 32 }}>
                        {(name || memberId).charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {name || "未知用户"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          真实姓名：{realName || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          手机号：{phone || "-"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", wordBreak: "break-all" }}
                        >
                          邮箱：{email || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                          ID：{memberId}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            车辆信息
                          </Typography>
                          {trucks.length > 0 ? (
                            <Stack spacing={0.75}>
                              {trucks.map((truck, index) => (
                                <Box
                                  key={truck.id || `${truck.plate}-${index}`}
                                  sx={{
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.75,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Typography variant="caption" fontWeight={600}>
                                      {truck.plate}
                                    </Typography>
                                    {truck.plateColor !== "-" ? (
                                      <Chip
                                        label={truck.plateColor}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: 11 }}
                                      />
                                    ) : null}
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                    车型：{truck.truckType} · 车长：{truck.truckLength}
                                  </Typography>
                                  {truck.phone ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                      随车电话：{truck.phone}
                                    </Typography>
                                  ) : null}
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              暂无车辆信息
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                暂无参与者
              </Typography>
            )}
          </Box>
        </Box>
        <Stack divider={<Divider flexItem />} sx={{ p: 0 }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ px: 2, py: 1.25 }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              创建时间
            </Typography>
            <Typography variant="body2">
              {detail.createdAt ? formatDate(detail.createdAt) : "-"}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ px: 2, py: 1.25 }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              更新时间
            </Typography>
            <Typography variant="body2">
              {detail.updatedAt ? formatDate(detail.updatedAt) : "-"}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ px: 2, py: 1.25 }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              回复数
            </Typography>
            <Typography variant="body2">{detail.replies.length}</Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderMessages = () => {
    if (!detail) {
      return null;
    }
    if (detail.replies.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={1}
        >
          <Typography variant="body2" color="text.secondary">
            暂无回复
          </Typography>
          <Typography variant="caption" color="text.secondary">
            在下方输入并发送第一条回复
          </Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={2}>
        {detail.replies.map((reply) => {
          const hasReplyUser = Boolean(reply.senderId);
          const fromSelf = isReplyFromCurrentUser(reply, currentUserId) || !hasReplyUser;
          const ai = isAiReply(reply);
          const senderUser = fromSelf
            ? currentUserRecord
            : memberUsersById[reply.senderId ?? ""] ?? null;
          const senderName = ai
            ? getSenderDisplayName(reply, currentUserId)
            : getUserDisplayName(senderUser) || reply.senderName || "我";
          const senderAvatar = ai
            ? reply.senderAvatar
            : getUserDisplayAvatar(senderUser) || reply.senderAvatar;
          const senderId = fromSelf
            ? ""
            : reply.senderId || getUserRecordId(senderUser);
          const shouldShowSenderId = !ai && hasReplyUser && Boolean(senderId);
          return (
            <Stack
              key={reply.id || `${reply.createdAt}-${senderName}`}
              direction={fromSelf ? "row-reverse" : "row"}
              spacing={1.5}
              alignItems="flex-start"
            >
              <Tooltip title={shouldShowSenderId ? `${senderName}（${senderId}）` : senderName}>
                <Avatar
                  src={senderAvatar || undefined}
                  sx={{
                    bgcolor: ai
                      ? "secondary.light"
                      : fromSelf
                      ? "primary.main"
                      : "grey.300",
                    color: ai || fromSelf ? "common.white" : "text.primary",
                    width: 32,
                    height: 32,
                    fontSize: 14,
                  }}
                >
                  {ai ? (
                    <SmartToyIcon fontSize="small" />
                  ) : (
                    senderName.charAt(0).toUpperCase()
                  )}
                </Avatar>
              </Tooltip>
              <Stack
                spacing={0.5}
                alignItems={fromSelf ? "flex-end" : "flex-start"}
                sx={{ minWidth: 0, maxWidth: "75%" }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ color: "text.secondary" }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {senderName}
                  </Typography>
                  {!ai && senderId ? (
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {senderId}
                    </Typography>
                  ) : null}
                  {ai ? (
                    <Chip
                      size="small"
                      color="info"
                      label="AI"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {reply.createdAt ? formatDate(reply.createdAt) : "-"}
                  </Typography>
                </Stack>
                <Stack
                  direction={fromSelf ? "row-reverse" : "row"}
                  spacing={1}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: "background.default",
                      maxWidth: "100%",
                      wordBreak: "break-word",
                    }}
                  >
                    {reply.content ? (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {reply.content}
                      </Typography>
                    ) : null}
                    {reply.images && reply.images.length > 0 ? (
                      <ImageList
                        cols={Math.min(reply.images.length, 3)}
                        gap={4}
                        rowHeight={80}
                        sx={{ mt: reply.content ? 1 : 0, width: "auto" }}
                      >
                        {reply.images.map((image, index) => (
                          <ImageListItem
                            key={`${reply.id}-image-${index}`}
                            sx={{
                              cursor: "pointer",
                              overflow: "hidden",
                              borderRadius: 1,
                            }}
                            onClick={() => openPreview(reply.images, index)}
                          >
                            <img
                              src={image}
                              alt={`消息图片 ${index + 1}`}
                              loading="lazy"
                              style={{ objectFit: "cover" }}
                            />
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : null}
                  </Paper>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="删除该条回复">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteReplyRequest(reply)}
                          disabled={deletingReplyId === reply.id}
                        >
                          {deletingReplyId === reply.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          );
        })}
        <div ref={messagesEndRef} />
      </Stack>
    );
  };

  const renderMessagesPanel = () => {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 520,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2">会话记录</Typography>
          <Typography variant="caption" color="text.secondary">
            {detail?.replies?.length ?? 0} 条
          </Typography>
        </Stack>
        <Box
          sx={{
            flex: 1,
            minHeight: 320,
            overflowY: "auto",
            p: 2,
            bgcolor: "background.default",
          }}
        >
          {renderMessages()}
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          {composerImages.length > 0 ? (
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap">
              {composerImages.map((image) => (
                <Box
                  key={image.id}
                  sx={{
                    position: "relative",
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveComposerImage(image.id)}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "common.white",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          ) : null}
          <TextField
            value={composerText}
            onChange={(event) => setComposerText(event.target.value)}
            placeholder="请输入回复内容"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            disabled={sending || !detail}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            mt={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* <Tooltip title={composerImages.length >= MAX_IMAGES ? "最多上传 3 张图片" : "添加图片"}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleImageButtonClick}
                    disabled={sending || !detail || composerImages.length >= MAX_IMAGES}
                  >
                    <AddPhotoAlternateIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                已选择 {composerImages.length}/{MAX_IMAGES} 张图片
              </Typography> */}
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={
                sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />
              }
              disabled={sending || !detail}
              onClick={handleSend}
            >
              {sending ? "发送中" : "发送回复"}
            </Button>
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFileChange}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={handleBack}
        >
          返回沟通管理
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} lg={3}>
          {renderConversationPanel()}
        </Grid>
        <Grid item xs={12} md={8} lg={9}>
          {renderMessagesPanel()}
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(pendingDeleteReply)}
        onClose={handleDeleteReplyCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            确认删除该条沟通记录？删除后不可恢复。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteReplyCancel} disabled={Boolean(deletingReplyId)}>
            取消
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteReplyConfirm}
            disabled={Boolean(deletingReplyId)}
          >
            {deletingReplyId ? "删除中" : "确认删除"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewImages.length > 0 && previewIndex >= 0}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        onKeyDown={handlePreviewKey}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1">图片预览</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="缩小">
              <span>
                <IconButton onClick={handlePreviewZoomOut} disabled={previewScale <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="放大">
              <span>
                <IconButton onClick={handlePreviewZoomIn} disabled={previewScale >= 3}>
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="左转 90°">
              <IconButton onClick={handlePreviewRotateLeft}>
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="右转 90°">
              <IconButton onClick={handlePreviewRotateRight}>
                <RotateRightIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="重置">
              <IconButton onClick={handlePreviewReset}>
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={closePreview}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "black",
            minHeight: 360,
          }}
        >
          {previewImages[previewIndex] ? (
            <img
              src={previewImages[previewIndex]}
              alt="预览"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                transform: `scale(${previewScale}) rotate(${previewRotation}deg)`,
                transformOrigin: "center center",
                transition: "transform 0.2s ease",
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CommunicationDetailPage;